<template>
  <div class="leave-report-print">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <h4 class="mb-0">Student Leave Report</h4>
      <div>
        <b-button variant="primary" @click="print" size="sm">
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          Print
        </b-button>
        <b-button variant="outline-secondary" @click="close" size="sm" class="ml-2">
          <feather-icon icon="XIcon" size="16" class="mr-50" />
          Close
        </b-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;" />
      <p class="mt-3 font-weight-medium">{{ loadingText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h5 class="text-danger font-weight-medium">{{ error }}</h5>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="page-header text-center">
        <h3 class="report-main-title">STUDENT LEAVE REPORT</h3>
        <div class="header-meta mt-1">
          <span><strong>Class:</strong> {{ className || 'All' }}</span>
          <span class="separator">|</span>
          <span><strong>Section:</strong> {{ sectionName || 'All' }}</span>
          <span class="separator">|</span>
          <span><strong>Status:</strong> {{ statusName || 'All' }}</span>
          <span class="separator">|</span>
          <span><strong>Generated:</strong> {{ currentDateTime }}</span>
        </div>
      </div>

      <!-- Main Table -->
      <table v-if="leaveRequests.length > 0" class="report-table">
        <thead>
          <tr>
            <th width="50" class="text-center">S#</th>
            <th width="100" class="text-center">STUDENT ID</th>
            <th>STUDENT INFORMATION</th>
            <th width="120" class="text-center">FROM DATE</th>
            <th width="120" class="text-center">TO DATE</th>
            <th width="70" class="text-center">DAYS</th>
            <th width="110" class="text-center">STATUS</th>
            <th width="140" class="text-center">REQ. DATE</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in leaveRequests" :key="item.id">
            <td class="text-center">{{ index + 1 }}</td>
            <td class="text-center font-weight-bold">{{ item.sID || item.localID || 'N/A' }}</td>
            <td>
              <div class="student-name">{{ item.name || item.studentName }}</div>
              <div class="student-meta-row">
                {{ item.meta_class || item.className || 'N/A' }} - {{ item.meta_section || item.sectionName || 'N/A' }}
              </div>
            </td>
            <td class="text-center">{{ formatDate(item.dtFrom) }}</td>
            <td class="text-center">{{ formatDate(item.dtTo) }}</td>
            <td class="text-center font-weight-bold">{{ item.totalDays }}</td>
            <td class="text-center">
              <span class="status-badge-pill" :class="getStatusBadgeClass(item.status)">
                {{ item.status }}
              </span>
            </td>
            <td class="text-center">{{ formatDateTime(item.date) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- No Data -->
      <div v-else class="text-center py-5 no-data">
        <h6 class="text-muted">No leave records found for the selected criteria.</h6>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small><strong>Printed By:</strong> {{ $store.state.userData.name }} | <strong>Date:</strong> {{ currentDateTime }} | <strong>Total Records:</strong> {{ leaveRequests.length }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'StudentLeaveReportPrint',
  components: {
    BButton,
    BSpinner
  },
  data() {
    return {
      loading: false,
      loadingText: 'Preparing report...',
      error: null,
      leaveRequests: [],
      campusName: '',
      className: '',
      sectionName: '',
      statusName: ''
    }
  },
  computed: {
    currentDateTime() {
      const now = new Date()
      return now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  created() {
    this.extractFilters()
    this.loadReportData()
  },
  methods: {
    extractFilters() {
      const q = this.$route.query
      this.campusName = q.campusName || ''
      this.className = q.className || ''
      this.sectionName = q.sectionName || ''
      this.statusName = q.statusName || ''
    },
    async loadReportData() {
      this.loading = true
      this.error = null
      try {
        const q = this.$route.query
        let url = `${this.$store.state.domain}StudentLeaveRequests/GetByPagination?`
        url += `db=${q.db}&cID=${q.cID}&pageNumber=1&pageSize=2000`
        
        // Add optional filters
        if (q.search) url += `&search=${encodeURIComponent(q.search)}`
        if (q.dateFrom) url += `&dateFrom=${q.dateFrom}`
        if (q.dateTo) url += `&dateTo=${q.dateTo}`
        if (q.status) url += `&status=${q.status}`
        if (q.classID) url += `&classID=${q.classID}`
        if (q.sectionID) url += `&sectionID=${q.sectionID}`
        if (q.sortBy) url += `&sortBy=${q.sortBy}`
        if (q.sortOrder) url += `&sortOrder=${q.sortOrder}`
        
        // Campus loadAll logic
        const campusID = parseInt(q.cID)
        url += `&loadAll=${campusID === 1 ? 'true' : 'false'}`
        
        // Today parameter for API requirement
        const dt = new Date().toISOString().split('T')[0]
        url += `&dt=${dt}`

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`
          }
        })

        if (response.data && response.data.data) {
          const data = response.data.data.data || response.data.data || []
          this.leaveRequests = Array.isArray(data) ? data : []
        } else {
          this.leaveRequests = []
        }
      } catch (err) {
        console.error('Print Report Error:', err)
        this.error = 'Failed to load report data. Please check your connection and try again.'
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    formatDateTime(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) + ' ' + d.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getStatusBadgeClass(status) {
      if (!status) return ''
      const s = status.toLowerCase()
      if (s === 'approved') return 'pill-approved'
      if (s === 'rejected') return 'pill-rejected'
      return 'pill-pending'
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  font-weight: 500;
}

.leave-report-print {
  background: #f0f0f0;
  min-height: 100vh;
  padding: 15px;
}

.control-bar {
  background: white;
  padding: 12px 20px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h4 {
    color: #000;
    font-weight: 600;
  }
}

.report-page {
  background: white;
  padding: 25px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 0 auto;
  max-width: 100%;
}

.page-header {
  margin-bottom: 25px;
  
  .report-main-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #000;
    margin-bottom: 8px;
    letter-spacing: 1px;
  }
  
  .header-meta {
    font-size: 0.9rem;
    color: #000;
    font-weight: 500;
    
    .separator {
      margin: 0 10px;
      color: #999;
    }
  }
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  
  thead {
    th {
      background: #f8fafc;
      padding: 10px 12px;
      border: 1px solid #000;
      color: #000;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
    }
  }
  
  tbody {
    tr {
      td {
        padding: 10px 12px;
        color: #000;
        border: 1px solid #000;
        vertical-align: middle;
        font-size: 0.85rem;
        line-height: 1.4;
      }
    }
  }
}

.student-name {
  font-weight: 700;
  font-size: 0.9rem;
}

.student-meta-row {
  font-size: 0.75rem;
  color: #444;
  margin-top: 2px;
}

.status-badge-pill {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  display: inline-block;
  border: 1px solid #ccc;
  min-width: 85px;
  text-align: center;
  
  &.pill-approved {
    background-color: #dcfce7;
    color: #16a34a;
    border-color: #86efac;
  }
  
  &.pill-rejected {
    background-color: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
  }
  
  &.pill-pending {
    background-color: #fef9c3;
    color: #ca8a04;
    border-color: #fde047;
  }
}

.page-footer {
  text-align: center;
  padding-top: 15px;
  margin-top: 20px;
  border-top: 1px solid #ccc;
  color: #555;
}

@media print {
  .no-print {
    display: none !important;
  }
  
  .leave-report-print {
    background: white;
    padding: 0;
    min-height: auto;
  }
  
  .report-page {
    box-shadow: none;
    border: none;
    padding: 10px;
    margin: 0;
  }

  .report-table {
    th, td {
      border: 1px solid #000 !important;
    }
    
    thead th {
      background-color: #f1f5f9 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
}
</style>

